import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import GoogleTranslate from './components/language/GoogleTranslate';

import Home from './pages/website/Home';
import Features from './pages/website/Features';
import Benefits from './pages/website/Benefits';
import HowToUse from './pages/website/HowToUse';
import Pricing from './pages/website/Pricing';
import Contact from './pages/website/Contact';
import ProjectInfo from './pages/website/ProjectInfo';
import Privacy from './pages/website/Privacy';
import Terms from './pages/website/Terms';
import Refund from './pages/website/Refund';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import Onboarding from './pages/auth/Onboarding';

import MerchantLayout from './pages/merchant/MerchantLayout';
import MerchantDashboard from './pages/merchant/MerchantDashboard';
import Customers from './pages/merchant/Customers';
import Products from './pages/merchant/Products';
import Categories from './pages/merchant/Categories';
import Inventory from './pages/merchant/Inventory';
import RoutesPage from './pages/merchant/Routes';
import Subscriptions from './pages/merchant/Subscriptions';
import Deliveries from './pages/merchant/Deliveries';
import Billing from './pages/merchant/Billing';
import Payments from './pages/merchant/Payments';
import Reports from './pages/merchant/Reports';
import AIAssistant from './pages/merchant/AIAssistant';
import RouteSheet from './pages/merchant/RouteSheet';
import MerchantSettings from './pages/merchant/Settings';

import CustomerLayout from './pages/customer/CustomerLayout';
import CustomerHome from './pages/customer/CustomerHome';
import CustomerSubs from './pages/customer/CustomerSubs';
import CustomerVacation from './pages/customer/CustomerVacation';
import CustomerDeliveries from './pages/customer/CustomerDeliveries';
import CustomerBills from './pages/customer/CustomerBills';
import CustomerProfile from './pages/customer/CustomerProfile';

import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMerchants from './pages/admin/AdminMerchants';
import AdminLeads from './pages/admin/AdminLeads';
import AdminPlans from './pages/admin/AdminPlans';

const isStandaloneApp = () => (
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true
);

const roleHome = (role, business) => {
  if (role === 'customer') return '/my';
  if (role === 'super_admin') return '/admin';
  if (role === 'merchant_owner' || role === 'merchant_manager') {
    return business ? '/app' : '/onboarding';
  }
  return '/onboarding';
};

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center text-rozops-text-muted">Loading…</div>
);

const RootRoute = () => {
  const { session, role, business, loading } = useAuth();
  if (!isStandaloneApp()) return <Home />;
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={roleHome(role, business)} replace />;
};

const PublicAuthRoute = ({ children }) => {
  const { session, role, business, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (session) return <Navigate to={roleHome(role, business)} replace />;
  return children;
};

const Guard = ({ roles }) => {
  const { session, role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!session) return <Navigate to="/login" replace />;
  if (roles && role && !roles.includes(role)) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GoogleTranslate />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/features" element={<Features />} />
          <Route path="/benefits" element={<Benefits />} />
          <Route path="/how-to-use" element={<HowToUse />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/project-info" element={<ProjectInfo />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/login" element={<PublicAuthRoute><Login /></PublicAuthRoute>} />
          <Route path="/register" element={<PublicAuthRoute><Register /></PublicAuthRoute>} />
          <Route path="/onboarding" element={<Onboarding />} />

          <Route element={<Guard roles={['merchant_owner', 'merchant_manager']} />}>
            <Route path="/app" element={<MerchantLayout />}>
              <Route index element={<MerchantDashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="products" element={<Products />} />
              <Route path="categories" element={<Categories />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="routes" element={<RoutesPage />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="deliveries" element={<Deliveries />} />
              <Route path="billing" element={<Billing />} />
              <Route path="payments" element={<Payments />} />
              <Route path="reports" element={<Reports />} />
              <Route path="ai" element={<AIAssistant />} />
              <Route path="route-sheet" element={<RouteSheet />} />
              <Route path="settings" element={<MerchantSettings />} />
            </Route>
          </Route>

          <Route element={<Guard roles={['customer']} />}>
            <Route path="/my" element={<CustomerLayout />}>
              <Route index element={<CustomerHome />} />
              <Route path="subscriptions" element={<CustomerSubs />} />
              <Route path="vacation" element={<CustomerVacation />} />
              <Route path="deliveries" element={<CustomerDeliveries />} />
              <Route path="bills" element={<CustomerBills />} />
              <Route path="profile" element={<CustomerProfile />} />
            </Route>
          </Route>

          <Route element={<Guard roles={['super_admin']} />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="merchants" element={<AdminMerchants />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="plans" element={<AdminPlans />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
